import React, { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { getPeriodComparison } from '../../lib/api';
import {
  Ship,
  Package,
  CheckCircle,
  TrendingUp,
} from 'lucide-react';

export function MonthlySummaryWidget({ data, loading }) {
  const { t } = useI18n();
  const [period1, setPeriod1] = useState('');
  const [period2, setPeriod2] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [comparing, setComparing] = useState(false);

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = d.toISOString().slice(0, 7);
      const label = d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  const monthOptions = getMonthOptions();

  const handleCompare = async () => {
    if (!period1 || !period2) return;
    setComparing(true);
    try {
      const res = await getPeriodComparison(period1, period2);
      setComparisonData(res.data);
    } catch (err) {
      console.error('Error comparing periods:', err);
    } finally {
      setComparing(false);
    }
  };

  if (loading && !comparisonData) {
    return (
      <div className="dashboard-card bg-card border border-border rounded-lg p-5">
        <h3 className="font-heading text-lg mb-4 text-foreground">{t('dashboard.periodComparison')}</h3>
        <div className="text-center py-8 text-muted-foreground animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  const displayData = comparisonData || data;
  const current = displayData?.period2 || displayData?.current_month || {};
  const previous = displayData?.period1 || displayData?.previous_month || {};
  const change = displayData?.change || {};

  const MetricRow = ({ icon: Icon, label, currentVal, prevVal, changePct, color }) => (
    <div className="flex items-center justify-between py-3 border-b border-border/30 last:border-0">
      <div className="flex items-center gap-2">
        <Icon size={16} className={color} />
        <span className="text-sm text-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-lg font-heading">{currentVal || 0}</span>
        <span className="text-sm text-muted-foreground">{prevVal || 0}</span>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
          changePct >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {changePct >= 0 ? '+' : ''}{changePct || 0}%
        </span>
      </div>
    </div>
  );

  return (
    <div className="dashboard-card glass-widget rounded-lg p-5 opacity-0 animate-scale-in stagger-5">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={20} className="text-emerald-400" />
        <h3 className="font-heading text-lg text-foreground">{t('dashboard.periodComparison')}</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-2 mb-4">
        <select 
          value={period1}
          onChange={(e) => setPeriod1(e.target.value)}
          className="bg-secondary border border-border rounded-lg px-3 py-2 text-sm text-foreground"
        >
          <option value="">{t('dashboard.previousMonth')}</option>
          {monthOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select 
          value={period2}
          onChange={(e) => setPeriod2(e.target.value)}
          className="bg-secondary border border-border rounded-lg px-3 py-2 text-sm text-foreground"
        >
          <option value="">{t('dashboard.currentMonth')}</option>
          {monthOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {(period1 || period2) && (
        <button 
          onClick={handleCompare}
          disabled={comparing}
          className="w-full mb-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {comparing ? t('dashboard.comparing') : t('dashboard.compare')}
        </button>
      )}

      <div className="text-xs text-muted-foreground flex justify-between mb-2 px-1">
        <span>{t('dashboard.metric')}</span>
        <div className="flex gap-4">
          <span className="text-primary">{current.label || t('dashboard.current')}</span>
          <span>{t('dashboard.previous')}</span>
          <span>{t('dashboard.change')}</span>
        </div>
      </div>

      <div className="bg-secondary/20 rounded-lg px-3">
        <MetricRow 
          icon={Package} 
          label={t('dashboard.containersArrived')} 
          currentVal={current.containers_arrived || current.containers} 
          prevVal={previous.containers_arrived || previous.containers}
          changePct={change.containers_arrived_pct || change.containers_pct}
          color="text-blue-400"
        />
        <MetricRow 
          icon={CheckCircle} 
          label={t('dashboard.containersDelivered')} 
          currentVal={current.containers_delivered || current.delivered} 
          prevVal={previous.containers_delivered || previous.delivered}
          changePct={change.containers_delivered_pct || change.delivered_pct}
          color="text-emerald-400"
        />
        <MetricRow 
          icon={Ship} 
          label={t('dashboard.newShipments')} 
          currentVal={current.new_shipments} 
          prevVal={previous.new_shipments}
          changePct={change.shipments_pct}
          color="text-amber-400"
        />
      </div>

      {comparisonData && (
        <button 
          onClick={() => setComparisonData(null)}
          className="w-full mt-3 text-xs text-muted-foreground hover:text-primary transition-colors"
        >
          {t('dashboard.backToAutoData')}
        </button>
      )}
    </div>
  );
}
